/**
 * Insurance Reports Component
 *
 * Provides reporting functionality for completed insurance leads:
 * - Salesperson Lead Report: Groups leads by salesperson
 * - Location Lead Report: Groups leads by dealership location
 * - Agent Lead Report: Groups leads by insurance agent who worked them
 * - All Archived Leads Report: Complete lead data for export
 *
 * Lead categories:
 * - Great: Quoted & Purchased insurance
 * - Good: Quoted but did not purchase
 * - Bad: Did not want a quote
 */

import React, { useState, useMemo } from 'react';
import { InsuranceLead } from '../../types/insurance';
import { getArchivedLeads, ArchivedLeadsResponse } from '../../services/insuranceApi';

type ReportType = 'salesperson' | 'location' | 'agent' | 'all_archived';

interface GroupedStats {
  groupName: string;
  totalLeads: number;
  great: number;
  good: number;
  bad: number;
  conversionRate: number;
  leads: InsuranceLead[];
}

interface InsuranceReportsProps {
  isAdmin?: boolean;
}

// Location mapping for filter dropdown
const LOCATIONS = [
  { id: 1, name: "Darling's Ford" },
  { id: 2, name: "Darling's Honda" },
  { id: 3, name: "Darling's Automall" },
  { id: 4, name: "Darling's Chrysler Dodge" },
  { id: 5, name: "Darling's Chevrolet" },
  { id: 6, name: "Darling's VW Audi" },
  { id: 7, name: "Green Light Used" },
  { id: 8, name: "Darling's Waterfront" },
  { id: 9, name: "Green Point" },
  { id: 10, name: "Darling's Kia" },
  { id: 11, name: "Darling's Nissan" },
  { id: 12, name: "Darling's Toyota" },
];

const getOutcomeLabel = (lead: InsuranceLead): string => {
  if (lead.outcome === 'quoted_purchased' || lead.outcome === 'purchased') return 'Quoted - Purchased';
  if (lead.outcome === 'quoted_not_purchased' || lead.outcome === 'not_purchased') return 'Quoted - Not Purchased';
  if (lead.outcome === 'bad_lead') return 'Bad Lead';
  return '';
};

const getOutcomeBadge = (lead: InsuranceLead) => {
  if (lead.outcome === 'quoted_purchased' || lead.outcome === 'purchased') {
    return <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">Quoted - Purchased</span>;
  }
  if (lead.outcome === 'quoted_not_purchased' || lead.outcome === 'not_purchased') {
    return <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">Quoted - Not Purchased</span>;
  }
  if (lead.outcome === 'bad_lead') {
    return <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">Bad Lead</span>;
  }
  return <span>-</span>;
};

const formatDateForExport = (dateStr: string) => {
  return new Date(dateStr).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const ConversionRateSpan: React.FC<{ rate: number }> = ({ rate }) => (
  <span className={`font-semibold ${rate >= 50 ? 'text-green-600' : rate >= 25 ? 'text-yellow-600' : 'text-red-600'}`}>
    {rate.toFixed(1)}%
  </span>
);

const SummaryTotals: React.FC<{ stats: GroupedStats[] }> = ({ stats }) => {
  const totalLeads = stats.reduce((sum, s) => sum + s.totalLeads, 0);
  const totalGreat = stats.reduce((sum, s) => sum + s.great, 0);
  const totalGood = stats.reduce((sum, s) => sum + s.good, 0);
  const totalBad = stats.reduce((sum, s) => sum + s.bad, 0);
  const quotedLeads = totalGreat + totalGood;
  const rate = quotedLeads > 0 ? (totalGreat / quotedLeads) * 100 : 0;

  return (
    <div className="px-6 py-4 bg-gray-100">
      <div className="flex flex-wrap gap-6 text-sm">
        <div>
          <span className="text-gray-500">Total Leads:</span>{' '}
          <span className="font-semibold">{totalLeads}</span>
        </div>
        <div>
          <span className="text-gray-500">Great:</span>{' '}
          <span className="font-semibold text-green-600">{totalGreat}</span>
        </div>
        <div>
          <span className="text-gray-500">Good:</span>{' '}
          <span className="font-semibold text-blue-600">{totalGood}</span>
        </div>
        <div>
          <span className="text-gray-500">Bad:</span>{' '}
          <span className="font-semibold text-red-600">{totalBad}</span>
        </div>
        <div>
          <span className="text-gray-500">Conversion:</span>{' '}
          <ConversionRateSpan rate={rate} />
        </div>
      </div>
    </div>
  );
};

const GroupHeader: React.FC<{ stats: GroupedStats; bgColor: string }> = ({ stats, bgColor }) => (
  <div className={`px-6 py-4 ${bgColor} flex flex-wrap items-center justify-between gap-4`}>
    <div className="flex items-center gap-4">
      <h4 className="text-lg font-semibold text-gray-900">{stats.groupName}</h4>
      <span className="text-sm text-gray-600">({stats.totalLeads} lead{stats.totalLeads !== 1 ? 's' : ''})</span>
    </div>
    <div className="flex flex-wrap gap-4 text-sm">
      <span className="text-green-600 font-medium">{stats.great} Great</span>
      <span className="text-blue-600 font-medium">{stats.good} Good</span>
      {stats.bad > 0 && <span className="text-red-600 font-medium">{stats.bad} Bad</span>}
      <ConversionRateSpan rate={stats.conversionRate} />
    </div>
  </div>
);

export const InsuranceReports: React.FC<InsuranceReportsProps> = ({ isAdmin = false }) => {
  const [reportType, setReportType] = useState<ReportType>('salesperson');
  const [startDate, setStartDate] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });
  const [locationId, setLocationId] = useState<number | null>(null);

  const [leads, setLeads] = useState<InsuranceLead[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasRun, setHasRun] = useState(false);

  const handleGenerateReport = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await getArchivedLeads(startDate, endDate, locationId || undefined);
      if (response.success && response.data) {
        setLeads(response.data.leads);
        setHasRun(true);
      } else {
        setError(response.error || 'Failed to fetch report data');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch report data');
    } finally {
      setIsLoading(false);
    }
  };

  const createGroupedStats = (
    leads: InsuranceLead[],
    getGroupName: (lead: InsuranceLead) => string
  ): GroupedStats[] => {
    const statsMap = new Map<string, GroupedStats>();

    leads.forEach((lead) => {
      const groupName = getGroupName(lead);
      if (!statsMap.has(groupName)) {
        statsMap.set(groupName, {
          groupName,
          totalLeads: 0,
          great: 0,
          good: 0,
          bad: 0,
          conversionRate: 0,
          leads: [],
        });
      }

      const stats = statsMap.get(groupName)!;
      stats.totalLeads++;
      stats.leads.push(lead);

      if (lead.outcome === 'quoted_purchased' || lead.outcome === 'purchased') {
        stats.great++;
      } else if (lead.outcome === 'quoted_not_purchased' || lead.outcome === 'not_purchased') {
        stats.good++;
      } else if (lead.outcome === 'bad_lead') {
        stats.bad++;
      }
    });

    const result = Array.from(statsMap.values());
    result.forEach((stats) => {
      const quotedLeads = stats.great + stats.good;
      stats.conversionRate = quotedLeads > 0
        ? (stats.great / quotedLeads) * 100
        : 0;
      stats.leads.sort((a, b) => {
        const dateA = new Date(a.completedAt || a.createdAt);
        const dateB = new Date(b.completedAt || b.createdAt);
        return dateB.getTime() - dateA.getTime();
      });
    });

    return result.sort((a, b) => b.totalLeads - a.totalLeads);
  };

  const salespersonStats = useMemo<GroupedStats[]>(() => {
    return createGroupedStats(leads, (lead) => lead.matchedData?.salesperson || 'Unknown');
  }, [leads]);

  const locationStats = useMemo<GroupedStats[]>(() => {
    return createGroupedStats(leads, (lead) => lead.locationName || 'Unknown');
  }, [leads]);

  const agentStats = useMemo<GroupedStats[]>(() => {
    return createGroupedStats(leads, (lead) => lead.claimedByName || 'Unassigned');
  }, [leads]);

  const generateGroupedCSV = (
    reportTitle: string,
    stats: GroupedStats[],
    getLeadColumns: (lead: InsuranceLead) => string[]
  ): string => {
    const totalLeads = stats.reduce((sum, s) => sum + s.totalLeads, 0);
    const totalGreat = stats.reduce((sum, s) => sum + s.great, 0);
    const totalGood = stats.reduce((sum, s) => sum + s.good, 0);
    const totalBad = stats.reduce((sum, s) => sum + s.bad, 0);
    const quotedLeads = totalGreat + totalGood;
    const overallRate = quotedLeads > 0 ? (totalGreat / quotedLeads) * 100 : 0;

    let csv = `${reportTitle}\n`;
    csv += `Date Range:,${startDate} to ${endDate}\n\n`;
    csv += `SUMMARY\n`;
    csv += `Total Leads,Great (Quoted & Purchased),Good (Quoted - Not Purchased),Bad (No Quote),Conversion\n`;
    csv += `${totalLeads},${totalGreat},${totalGood},${totalBad},${overallRate.toFixed(1)}%\n\n`;

    stats.forEach((group) => {
      csv += `\n"${group.groupName}",${group.totalLeads} leads,${group.great} Great,${group.good} Good,${group.bad} Bad,${group.conversionRate.toFixed(1)}% Conversion\n`;

      if (reportType === 'salesperson') {
        csv += `Customer,Stock Number,Location,Outcome,Completed,Claimed By\n`;
      } else if (reportType === 'location') {
        csv += `Customer,Stock Number,Salesperson,Outcome,Completed,Claimed By\n`;
      } else if (reportType === 'agent') {
        csv += `Customer,Stock Number,Location,Salesperson,Outcome,Completed\n`;
      }

      group.leads.forEach((lead) => {
        csv += getLeadColumns(lead).join(',') + '\n';
      });
    });

    return csv;
  };

  const handleExportCSV = () => {
    let csvContent = '';
    let filename = '';

    if (reportType === 'salesperson') {
      csvContent = generateGroupedCSV(
        'SALESPERSON LEAD REPORT',
        salespersonStats,
        (lead) => [
          `"${lead.sourceData.customerName || ''}"`,
          lead.stockNumber,
          `"${lead.locationName}"`,
          getOutcomeLabel(lead),
          formatDateForExport(lead.completedAt || lead.updatedAt),
          `"${lead.claimedByName || ''}"`,
        ]
      );
      filename = `salesperson_lead_report_${startDate}_to_${endDate}.csv`;
    } else if (reportType === 'location') {
      csvContent = generateGroupedCSV(
        'LOCATION LEAD REPORT',
        locationStats,
        (lead) => [
          `"${lead.sourceData.customerName || ''}"`,
          lead.stockNumber,
          `"${lead.matchedData?.salesperson || ''}"`,
          getOutcomeLabel(lead),
          formatDateForExport(lead.completedAt || lead.updatedAt),
          `"${lead.claimedByName || ''}"`,
        ]
      );
      filename = `location_lead_report_${startDate}_to_${endDate}.csv`;
    } else if (reportType === 'agent') {
      csvContent = generateGroupedCSV(
        'AGENT LEAD REPORT',
        agentStats,
        (lead) => [
          `"${lead.sourceData.customerName || ''}"`,
          lead.stockNumber,
          `"${lead.locationName}"`,
          `"${lead.matchedData?.salesperson || ''}"`,
          getOutcomeLabel(lead),
          formatDateForExport(lead.completedAt || lead.updatedAt),
        ]
      );
      filename = `agent_lead_report_${startDate}_to_${endDate}.csv`;
    } else {
      csvContent = 'Lead ID,Customer Name,Stock Number,Location,Salesperson,Outcome,Created At,Completed At,Claimed By,Vehicle,VIN,Notes\n';
      leads.forEach((lead) => {
        const vehicle = lead.matchedData
          ? `${lead.matchedData.vehicleYear || ''} ${lead.matchedData.vehicleMake || ''} ${lead.matchedData.vehicleModel || ''}`.trim()
          : '';
        const vin = lead.matchedData?.vin || lead.manualEntry?.vin || '';
        csvContent += [
          lead.leadId,
          `"${lead.sourceData.customerName || ''}"`,
          lead.stockNumber,
          `"${lead.locationName}"`,
          `"${lead.matchedData?.salesperson || ''}"`,
          getOutcomeLabel(lead),
          lead.createdAt,
          lead.completedAt || '',
          `"${lead.claimedByName || ''}"`,
          `"${vehicle}"`,
          vin,
          `"${(lead.notes || '').replace(/"/g, '""')}"`,
        ].join(',') + '\n';
      });
      filename = `all_archived_leads_${startDate}_to_${endDate}.csv`;
    }

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const renderGroupedReport = (
    stats: GroupedStats[],
    headerBgColor: string,
    columns: { label: string; render: (lead: InsuranceLead) => React.ReactNode }[]
  ) => (
    <div className="divide-y divide-gray-200">
      <SummaryTotals stats={stats} />
      {stats.map((groupStats) => (
        <div key={groupStats.groupName} className="border-b border-gray-200 last:border-b-0">
          <GroupHeader stats={groupStats} bgColor={headerBgColor} />
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  {columns.map((col) => (
                    <th key={col.label} className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {groupStats.leads.map((lead) => (
                  <tr key={lead.leadId} className="hover:bg-gray-50">
                    {columns.map((col, i) => (
                      <td key={i} className="px-6 py-2 text-sm text-gray-900">
                        {col.render(lead)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );

  const customerCol = {
    label: 'Customer',
    render: (lead: InsuranceLead) => (
      <>
        <div className="font-medium">{lead.sourceData.customerName}</div>
        <div className="text-xs text-gray-500">Stock #{lead.stockNumber}</div>
      </>
    ),
  };
  const outcomeCol = { label: 'Outcome', render: (lead: InsuranceLead) => getOutcomeBadge(lead) };
  const completedCol = { label: 'Completed', render: (lead: InsuranceLead) => formatDateForExport(lead.completedAt || lead.updatedAt) };
  const claimedByCol = { label: 'Claimed By', render: (lead: InsuranceLead) => lead.claimedByName || '-' };
  const locationCol = { label: 'Location', render: (lead: InsuranceLead) => lead.locationName };
  const salespersonCol = { label: 'Salesperson', render: (lead: InsuranceLead) => lead.matchedData?.salesperson || '-' };

  return (
    <div className="space-y-6">
      {/* Report Parameters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Report Parameters</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Report Type</label>
            <select
              value={reportType}
              onChange={(e) => setReportType(e.target.value as ReportType)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="salesperson">Salesperson Lead Report</option>
              <option value="location">Location Lead Report</option>
              <option value="agent">Agent Lead Report</option>
              <option value="all_archived">All Archived Leads</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Location (optional)</label>
            <select
              value={locationId || ''}
              onChange={(e) => setLocationId(e.target.value ? parseInt(e.target.value) : null)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Locations</option>
              {LOCATIONS.map((loc) => (
                <option key={loc.id} value={loc.id}>{loc.name}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-4 flex gap-3">
          <button
            onClick={handleGenerateReport}
            disabled={isLoading}
            className="px-4 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 disabled:bg-blue-400 flex items-center gap-2"
          >
            {isLoading ? (
              <>
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Generating...
              </>
            ) : (
              'Generate Report'
            )}
          </button>

          {hasRun && leads.length > 0 && (
            <button
              onClick={handleExportCSV}
              className="px-4 py-2 bg-green-600 text-white font-medium rounded-md hover:bg-green-700 flex items-center gap-2"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Export to Excel (CSV)
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="p-4 bg-red-50 text-red-700 rounded-lg">
          <p className="font-medium">Error</p>
          <p className="text-sm">{error}</p>
        </div>
      )}

      {hasRun && !isLoading && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">
              {reportType === 'salesperson' && 'Salesperson Lead Report'}
              {reportType === 'location' && 'Location Lead Report'}
              {reportType === 'agent' && 'Agent Lead Report'}
              {reportType === 'all_archived' && 'All Archived Leads'}
            </h3>
            <span className="text-sm text-gray-500">
              {leads.length} lead{leads.length !== 1 ? 's' : ''} found
            </span>
          </div>

          {leads.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No completed leads found for the selected date range.
            </div>
          ) : reportType === 'salesperson' ? (
            renderGroupedReport(salespersonStats, 'bg-blue-50', [customerCol, locationCol, outcomeCol, completedCol, claimedByCol])
          ) : reportType === 'location' ? (
            renderGroupedReport(locationStats, 'bg-green-50', [customerCol, salespersonCol, outcomeCol, completedCol, claimedByCol])
          ) : reportType === 'agent' ? (
            renderGroupedReport(agentStats, 'bg-purple-50', [customerCol, locationCol, salespersonCol, outcomeCol, completedCol])
          ) : (
            /* All Archived Leads Table */
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Salesperson</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Outcome</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Completed</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Claimed By</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {leads.map((lead, index) => (
                    <tr key={lead.leadId} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-gray-900">{lead.sourceData.customerName}</div>
                        <div className="text-xs text-gray-500">Stock #{lead.stockNumber}</div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">{lead.locationName}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{lead.matchedData?.salesperson || '-'}</td>
                      <td className="px-4 py-3">{getOutcomeBadge(lead)}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{formatDateForExport(lead.completedAt || lead.updatedAt)}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{lead.claimedByName || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default InsuranceReports;
